import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  FileCheck,
  Shield,
  Languages,
  MessageSquare,
  Globe,
  Target,
  CheckCircle2,
  ArrowRight,
  Clock,
  Users,
  TrendingUp,
  HelpCircle
} from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

export const ServicesPage = () => {
  const services = [
    {
      title: 'CTD/ACTD Dossier Preparation',
      icon: FileCheck,
      color: '#606090',
      solves: [
        'Unclear documentation structure',
        'Missing modules or mismatched formats',
        'Rejections due to formatting or incomplete data'
      ],
      deliverables: [
        'Complete Module 1–5 CTD/ACTD dossier',
        'Soft copy review + technical file structuring',
        'Technical write-ups, QOS, product summaries',
        'Country-specific adaptation for GCC, LATAM, Africa, ASEAN'
      ],
      timeline: 'Varies by formulation & documentation availability.'
    },
    {
      title: 'API Documentation & DMF Support',
      icon: Shield,
      color: '#7090A0',
      solves: [
        'Gaps in API supplier documentation',
        'Delays caused by missing DMF sections',
        'Mismatch between finished product & API documentation'
      ],
      deliverables: [
        'API/DMF data checklist',
        'COAs, MSDS, stability data, Q3/Q4 alignment',
        'Supplier coordination for missing sections',
        'Final integrated API documentation set'
      ],
      timeline: 'Depends on API manufacturer responsiveness.'
    },
    {
      title: 'Label, Artwork & Translation Compliance',
      icon: Languages,
      color: '#A060B0',
      solves: [
        'Incorrect claims or non-compliant statements',
        'Missing mandatory text in local languages',
        'Errors that trigger submission delays'
      ],
      deliverables: [
        'Label & pack insert review',
        'Compliance checklist (region-specific)',
        'Artwork redesign (optional)',
        'Translations: Arabic, French, Burmese, Spanish, Portuguese'
      ],
      timeline: 'Fast-track possible.'
    },
    {
      title: 'Deficiency & Query Handling',
      icon: MessageSquare,
      color: '#708080',
      solves: [
        'Rejections due to unclear responses',
        'Misinterpretation of MOH requirements',
        'Delays caused by missing clarifications'
      ],
      deliverables: [
        'MOH query analysis',
        'Response drafting',
        'Evidence document preparation',
        'Coordination with importer/distributor'
      ],
      timeline: 'Immediate priority support.'
    },
    {
      title: 'Local Distributor Coordination',
      icon: Globe,
      color: '#606090',
      solves: [
        'Misalignment between manufacturer & importer',
        'Incorrect submission formats',
        'Delays during submission filing'
      ],
      deliverables: [
        'Submission kit for importer',
        'Pre-filing document checks',
        'Communication channel setup',
        'Compliance alignment meetings'
      ],
      timeline: 'Continuous support until submission.'
    },
    {
      title: 'Add-ons',
      icon: Target,
      color: '#7090A0',
      solves: [
        'Product teams needing additional presentation-ready or submission-ready assets.'
      ],
      deliverables: [
        'Artwork design (regulatory-safe)',
        'eCTD formatting',
        'Technical brochures',
        'Product monographs',
        'Label claim review'
      ],
      timeline: 'Project-specific.'
    }
  ];

  const benefits = [
    {
      title: 'Checklist-Driven Processes',
      description: 'Reduces errors, rework and unstructured documentation.',
      icon: CheckCircle2,
      color: '#606090'
    },
    {
      title: 'Senior-Level Oversight',
      description: 'Every critical module is reviewed by a regulatory specialist.',
      icon: Users,
      color: '#7090A0'
    },
    {
      title: 'Region-Focused Expertise',
      description: 'GCC, LATAM, Africa and Asia have different expectations — we prepare documents accordingly.',
      icon: Globe,
      color: '#A060B0'
    }
  ];

  const processSteps = [
    {
      number: '1',
      title: 'Intake & Document Harvest',
      description: 'We collect available documents and map what is missing.',
      color: '#606090'
    },
    {
      number: '2',
      title: 'Compliance Review',
      description: 'We crosscheck requirements of the target country MOH.',
      color: '#7090A0'
    },
    {
      number: '3',
      title: 'Document Drafting & Finalization',
      description: 'Modules, API sections, artwork, translations, technical write-ups.',
      color: '#A060B0'
    },
    {
      number: '4',
      title: 'Submission-Ready Finalization',
      description: 'Aligned with importer or local representative format.',
      color: '#708080'
    },
    {
      number: '5',
      title: 'Support During & After Submission',
      description: 'Query handling, clarifications, and lifecycle updates.',
      color: '#606090'
    }
  ];

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>CTD ACTD Dossier Preparation Services | Pharma Regulatory Consultancy India</title>
        <meta
          name="description"
          content="Expert CTD/ACTD dossier preparation, API/DMF documentation, artwork compliance, regulatory translations for Africa, Asia, GCC exports. End-to-end pharma regulatory services."
        />
        <meta name="keywords" content="CTD dossier preparation, ACTD dossier preparation, pharmaceutical regulatory services, API DMF documentation, artwork compliance, pharma regulatory consultancy India, regulatory affairs outsourcing, dossier compilation services, label compliance Africa Asia" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://www.theapostrophe.com/services" />
        
        {/* Open Graph */}
        <meta property="og:title" content="CTD/ACTD Dossier Preparation | Pharma Regulatory Services" />
        <meta property="og:description" content="Expert dossier preparation, artwork compliance, and regulatory support for pharma exports to Africa, Asia, GCC." />
        <meta property="og:url" content="https://www.theapostrophe.com/services" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.theapostrophe.com/og-image.jpg" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="CTD/ACTD Dossier Preparation | Pharma Regulatory Services" />
        <meta name="twitter:description" content="Expert dossier preparation, artwork compliance, and regulatory support for pharma exports to Africa, Asia, GCC." />
        
        {/* Schema - Service */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Pharmaceutical Regulatory Services",
            "provider": {
              "@type": "Organization",
              "name": "TheApostrophe"
            },
            "areaServed": ["Africa", "Asia", "GCC", "Latin America"],
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Regulatory Services",
              "itemListElement": [
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "CTD/ACTD Dossier Preparation"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "API/DMF Documentation"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Artwork and Label Compliance"
                  }
                }
              ]
            }
          })}
        </script>
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What is the difference between CTD and ACTD dossier formats?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "CTD (Common Technical Document) is a standardized format used globally, especially in GCC, LATAM, and some African markets. ACTD (ASEAN Common Technical Document) is specific to ASEAN countries like Cambodia, Myanmar, and Sri Lanka. Both follow a 5-module structure but differ in Module 1 (administrative requirements). We adapt your dossier to match the exact format required by your target market."
                }
              },
              {
                "@type": "Question",
                "name": "Do you handle nutraceutical registration differently from pharmaceutical products?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes. Nutraceutical registration often follows simplified pathways in some countries but requires detailed ingredient acceptability checks, claim validation, and category classification (food vs supplement vs therapeutic). We ensure your product is classified correctly and documentation matches local expectations for supplements, health foods, or functional foods."
                }
              },
              {
                "@type": "Question",
                "name": "How long does CTD dossier preparation typically take?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Timeline depends on formulation complexity and documentation availability. For products with complete data, a basic CTD dossier takes 3-4 weeks. Complex formulations or products requiring GAP analysis and additional testing may take 6-8 weeks. We provide a detailed timeline after initial document review."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="pt-32 pb-24 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <Badge className="mb-6 px-6 py-2 text-sm" style={{ backgroundColor: '#A060B0', color: 'white' }}>
            Our Services
          </Badge>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-8 leading-tight" style={{ color: '#303040' }}>
            Regulatory Services for Pharma & Nutraceutical Exports
          </h1>
          <p className="text-2xl mb-12 leading-relaxed" style={{ color: '#708080' }}>
            Specialised support for CTD/ACTD filings, artwork compliance, API/DMF documentation, translations, and query handling across LATAM, GCC, Africa & Asia.
          </p>
          <Link to="/contact">
            <Button
              size="lg"
              className="text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105 hover:shadow-xl"
              style={{ backgroundColor: '#606090', color: 'white' }}
            >
              Get a Services Proposal
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Service Overview */}
      <section className="py-24 px-6" style={{ backgroundColor: 'rgba(112, 128, 128, 0.05)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#303040' }}>
              What We Offer
            </h2>
            <p className="text-xl max-w-4xl mx-auto" style={{ color: '#708080' }}>
              Each service is designed to reduce deficiencies, eliminate documentation confusion, and prepare your product for smooth submission in regulated and semi-regulated markets.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <Card
                key={index}
                className="border-0 shadow-xl hover:shadow-2xl transition-all duration-500 rounded-3xl overflow-hidden"
                style={{ backgroundColor: 'white' }}
              >
                <div className="h-2" style={{ backgroundColor: service.color }} />
                <CardHeader className="pb-4">
                  <div className="flex items-center gap-4 mb-4">
                    <div
                      className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: service.color + '20' }}
                    >
                      <service.icon className="h-7 w-7" style={{ color: service.color }} />
                    </div>
                    <CardTitle className="text-2xl" style={{ color: '#303040' }}>
                      {service.title}
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* What It Solves */}
                  <div>
                    <h3 className="font-bold mb-3 flex items-center gap-2" style={{ color: service.color }}>
                      <Target className="h-4 w-4" />
                      What It Solves
                    </h3>
                    <ul className="space-y-2">
                      {service.solves.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm" style={{ color: '#708080' }}>
                          <span className="text-red-500 mt-1">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Deliverables */}
                  <div>
                    <h3 className="font-bold mb-3 flex items-center gap-2" style={{ color: service.color }}>
                      <CheckCircle2 className="h-4 w-4" />
                      Deliverables
                    </h3>
                    <ul className="space-y-2">
                      {service.deliverables.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm" style={{ color: '#708080' }}>
                          <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: service.color }} />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Timeline */}
                  <div>
                    <h3 className="font-bold mb-2 flex items-center gap-2" style={{ color: service.color }}>
                      <Clock className="h-4 w-4" />
                      Timeline
                    </h3>
                    <p className="text-sm" style={{ color: '#708080' }}>
                      {service.timeline}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Our Services Work */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#303040' }}>
              Built for Fast, Error-Free Approvals
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <Card
                key={index}
                className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 rounded-3xl p-10 text-center"
                style={{ backgroundColor: 'white' }}
              >
                <div
                  className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 transition-transform duration-300 hover:scale-110"
                  style={{ backgroundColor: benefit.color + '20' }}
                >
                  <benefit.icon className="h-10 w-10" style={{ color: benefit.color }} />
                </div>
                <h3 className="text-2xl font-bold mb-4" style={{ color: '#303040' }}>
                  {benefit.title}
                </h3>
                <p className="text-lg leading-relaxed" style={{ color: '#708080' }}>
                  {benefit.description}
                </p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-24 px-6" style={{ backgroundColor: 'rgba(96, 96, 144, 0.05)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#303040' }}>
              Structured Process. Predictable Outcomes.
            </h2>
          </div>
          <div className="grid md:grid-cols-5 gap-6">
            {processSteps.map((step, index) => (
              <div key={index} className="relative">
                <Card
                  className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 rounded-2xl p-6 h-full"
                  style={{ backgroundColor: 'white' }}
                >
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 mx-auto"
                    style={{ backgroundColor: step.color }}
                  >
                    <span className="text-2xl font-bold text-white">{step.number}</span>
                  </div>
                  <h3 className="text-lg font-bold mb-3 text-center" style={{ color: '#303040' }}>
                    {step.title}
                  </h3>
                  <p className="text-sm text-center leading-relaxed" style={{ color: '#708080' }}>
                    {step.description}
                  </p>
                </Card>
                {index < processSteps.length - 1 && (
                  <div className="hidden md:block absolute -right-3 top-1/2 transform -translate-y-1/2 z-10">
                    <ArrowRight className="h-6 w-6" style={{ color: '#708080' }} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Services & Resources */}
      <section className="py-24 px-6" style={{ backgroundColor: 'rgba(112, 128, 128, 0.05)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#303040' }}>
              Tailored Solutions for Your Export Strategy
            </h2>
            <p className="text-xl" style={{ color: '#708080' }}>
              Explore our packaged services and regional expertise
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <Link to="/success-packs">
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 rounded-2xl p-8 h-full cursor-pointer group">
                <div className="flex flex-col h-full">
                  <h3 className="text-2xl font-bold mb-4 group-hover:text-opacity-80 transition-colors" style={{ color: '#A060B0' }}>
                    Success Packs
                  </h3>
                  <p className="mb-6 flex-grow" style={{ color: '#708080' }}>
                    Complete market entry packages from <strong>groundwork analysis</strong> to <strong>lifecycle management</strong>
                  </p>
                  <div className="flex items-center gap-2" style={{ color: '#A060B0' }}>
                    <span className="font-semibold">Explore Packs</span>
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-2 transition-transform" />
                  </div>
                </div>
              </Card>
            </Link>
            <Link to="/regions">
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 rounded-2xl p-8 h-full cursor-pointer group">
                <div className="flex flex-col h-full">
                  <h3 className="text-2xl font-bold mb-4 group-hover:text-opacity-80 transition-colors" style={{ color: '#7090A0' }}>
                    Regional Expertise
                  </h3>
                  <p className="mb-6 flex-grow" style={{ color: '#708080' }}>
                    <strong>Africa, Asia, GCC, LATAM</strong> — region-specific regulatory strategies and <strong>CTD/ACTD dossier</strong> alignment
                  </p>
                  <div className="flex items-center gap-2" style={{ color: '#7090A0' }}>
                    <span className="font-semibold">View Regions</span>
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-2 transition-transform" />
                  </div>
                </div>
              </Card>
            </Link>
            <Link to="/countries">
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 rounded-2xl p-8 h-full cursor-pointer group">
                <div className="flex flex-col h-full">
                  <h3 className="text-2xl font-bold mb-4 group-hover:text-opacity-80 transition-colors" style={{ color: '#606090' }}>
                    Country Guides
                  </h3>
                  <p className="mb-6 flex-grow" style={{ color: '#708080' }}>
                    <strong>30+ country-specific</strong> regulatory guides with timelines, requirements, and <strong>dossier formats</strong>
                  </p>
                  <div className="flex items-center gap-2" style={{ color: '#606090' }}>
                    <span className="font-semibold">Browse Countries</span>
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-2 transition-transform" />
                  </div>
                </div>
              </Card>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#303040' }}>
              Frequently Asked Questions
            </h2>
            <p className="text-xl" style={{ color: '#708080' }}>
              Common questions about our pharmaceutical regulatory services
            </p>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="faq-1" className="border-0 rounded-2xl overflow-hidden bg-white shadow-md">
              <AccordionTrigger className="px-8 py-6 hover:no-underline text-left">
                <span className="text-lg font-bold pr-8" style={{ color: '#303040' }}>
                  What is the difference between CTD and ACTD dossier formats?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-8 pb-6">
                <p className="text-lg leading-relaxed" style={{ color: '#708080' }}>
                  <strong>CTD (Common Technical Document)</strong> is a standardized format used globally, especially in GCC, LATAM, and some African markets. <strong>ACTD (ASEAN Common Technical Document)</strong> is specific to ASEAN countries like Cambodia, Myanmar, and Sri Lanka. Both follow a 5-module structure but differ in Module 1 (administrative requirements). We adapt your dossier to match the exact format required by your target market.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-2" className="border-0 rounded-2xl overflow-hidden bg-white shadow-md">
              <AccordionTrigger className="px-8 py-6 hover:no-underline text-left">
                <span className="text-lg font-bold pr-8" style={{ color: '#303040' }}>
                  Do you handle nutraceutical registration differently from pharmaceutical products?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-8 pb-6">
                <p className="text-lg leading-relaxed" style={{ color: '#708080' }}>
                  Yes. <strong>Nutraceutical registration</strong> often follows simplified pathways in some countries but requires detailed <strong>ingredient acceptability checks</strong>, claim validation, and category classification (food vs supplement vs therapeutic). We ensure your product is classified correctly and documentation matches local expectations for supplements, health foods, or functional foods.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-3" className="border-0 rounded-2xl overflow-hidden bg-white shadow-md">
              <AccordionTrigger className="px-8 py-6 hover:no-underline text-left">
                <span className="text-lg font-bold pr-8" style={{ color: '#303040' }}>
                  How long does CTD dossier preparation typically take?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-8 pb-6">
                <p className="text-lg leading-relaxed" style={{ color: '#708080' }}>
                  Timeline depends on <strong>formulation complexity</strong> and <strong>documentation availability</strong>. For products with complete data, a basic CTD dossier takes 3-4 weeks. Complex formulations or products requiring GAP analysis and additional testing may take 6-8 weeks. We provide a detailed timeline after initial document review.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-4" className="border-0 rounded-2xl overflow-hidden bg-white shadow-md">
              <AccordionTrigger className="px-8 py-6 hover:no-underline text-left">
                <span className="text-lg font-bold pr-8" style={{ color: '#303040' }}>
                  Do you provide artwork and label compliance services for all languages?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-8 pb-6">
                <p className="text-lg leading-relaxed" style={{ color: '#708080' }}>
                  Yes. We provide <strong>artwork compliance</strong> and <strong>regulatory translations</strong> in Arabic (GCC), Spanish (LATAM), French (West Africa), Portuguese, Burmese, Khmer, and other regional languages. Our translations are regulatory-grade with mandatory text verification by native regulatory consultants to ensure compliance with local <strong>labeling requirements</strong>.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-5" className="border-0 rounded-2xl overflow-hidden bg-white shadow-md">
              <AccordionTrigger className="px-8 py-6 hover:no-underline text-left">
                <span className="text-lg font-bold pr-8" style={{ color: '#303040' }}>
                  What is included in your API/DMF documentation support?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-8 pb-6">
                <p className="text-lg leading-relaxed" style={{ color: '#708080' }}>
                  Our <strong>API and DMF support</strong> includes: API master file compilation, COA and MSDS verification, stability data alignment, impurity profiling (Q3/Q4), supplier coordination for missing sections, and final integrated API documentation that aligns with your finished product dossier. This prevents rejections caused by API-FP mismatches.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-6" className="border-0 rounded-2xl overflow-hidden bg-white shadow-md">
              <AccordionTrigger className="px-8 py-6 hover:no-underline text-left">
                <span className="text-lg font-bold pr-8" style={{ color: '#303040' }}>
                  Can you help if we receive deficiencies or queries from the Ministry of Health?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-8 pb-6">
                <p className="text-lg leading-relaxed" style={{ color: '#708080' }}>
                  Absolutely. Our <strong>deficiency and query handling</strong> service includes: MOH query analysis, technical response drafting, evidence document preparation, and coordination with your local importer or distributor. We ensure responses are technically accurate, complete, and aligned with regulator expectations to secure approval quickly.
                </p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <Card
            className="border-0 shadow-2xl rounded-3xl p-12 md:p-16 text-center"
            style={{ backgroundColor: '#303040' }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
              Ready to Prepare Your Product for International Submission?
            </h2>
            <p className="text-xl mb-10 text-white/80">
              Let's discuss your regulatory needs and create a tailored service plan.
            </p>
            <Link to="/contact">
              <Button
                size="lg"
                className="text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105"
                style={{ backgroundColor: '#A060B0', color: 'white' }}
              >
                Get a Services Proposal
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </Card>
        </div>
      </section>
    </div>
  );
};
